import React, { useRef } from 'react';
import { FocusableOptions, useFocusable } from 'react-aria';

type FocusableTriggerProps = {
  /** Identifies the element (or elements) whose contents or presence are controlled by the current element. */
  'aria-controls'?: string;
  /** Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed. */
  'aria-expanded'?: boolean;
  /** Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element. */
  'aria-haspopup'?:
    | boolean
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog'
    | 'true'
    | 'false';
  /** Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed. */
  'aria-label'?: string;
  /** Defines a string value that labels the current element. */
  children: React.ReactNode;
  /** Optional property for overriding styles with CSS-in-JS. */
  className?: string;
  /** Hanlder called when the trigger is clicked. */
  onClick?: (event: React.MouseEvent) => void;
  /** Handler called when the focus state of the child changes. */
  onFocusChange?: FocusableOptions['onFocusChange'];
  /** Style object for the wrapper element */
  style?: React.CSSProperties;
  /** Name of the HTML tag used to wrap `children`. */
  tag?: 'button' | 'div' | 'summary';
};
/**
 * An optional wrapper around an element that should trigger the tooltip's visibility. This component should only be used when the elements aren't otherwise focusable using a keyboard.
 */
export const FocusableTrigger = ({
  children,
  className,
  onClick,
  onFocusChange,
  style,
  tag = 'div',
  ...props
}: FocusableTriggerProps) => {
  const ref = useRef(null);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { focusableProps } = useFocusable({ onFocusChange }, ref);
  const Tag = tag;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <Tag
      {...props}
      {...focusableProps}
      className={className}
      data-zds
      onClick={onClick}
      ref={ref}
      style={style}
      tabIndex={0}
    >
      {children}
    </Tag>
  );
};
